import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda } from '../state/format';
import { LineaBolsa } from '../types';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');
const MAX_FILAS_VISIBLES = 5;

interface QuoteLinesProps {
  lineas: LineaBolsa[];
  total: number;
  onVerEdicion: () => void;
}

function iniciales(nombre: string): string {
  return (nombre.trim()[0] ?? '?').toUpperCase();
}

interface FilaLineaProps {
  linea: LineaBolsa;
  nombreMarcacion?: string;
}

const FilaLinea: React.FC<FilaLineaProps> = ({ linea, nombreMarcacion }) => {
  const [imagenRota, setImagenRota] = useState(false);
  const nombre = linea.nombre_producto ?? 'Producto';

  const partesMeta = [
    linea.color_seleccionado,
    linea.tipo_marcacion_id != null ? nombreMarcacion : null,
    `${linea.cantidad} und × ${formatMoneda(linea.precio_unitario)}`,
  ].filter((p): p is string => Boolean(p && p.trim().length > 0));

  return (
    <Box
      data-testid="quote-line-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 0',
        borderBottom: `1px solid ${TOKENS.hair}`,
      }}
    >
      {linea.imagen_url && !imagenRota ? (
        <Box
          component="img"
          src={linea.imagen_url}
          alt={nombre}
          loading="lazy"
          onError={() => setImagenRota(true)}
          sx={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            backgroundColor: TOKENS.wash,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), fontWeight: 700, color: TOKENS.muted }}>
            {iniciales(nombre)}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: fs(12.5),
            fontWeight: 600,
            color: TOKENS.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {nombre}
        </Typography>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>
          {partesMeta.join(' · ')}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink, flexShrink: 0 }}>
        {formatMoneda(linea.subtotal)}
      </Typography>
    </Box>
  );
};

// SPEC-103 — sección de productos del drawer: solo lectura (RN-002), scroll interno
// de 5 filas (RN-... §2.3 spec fuente), TOTAL VENTA = bolsa.total (RN-001, nunca la suma calculada).
export const QuoteLines: React.FC<QuoteLinesProps> = ({ lineas, total, onVerEdicion }) => {
  const [nombresMarcacion, setNombresMarcacion] = useState<Record<number, string>>({});

  useEffect(() => {
    const hayMarcaciones = lineas.some((l) => l.tipo_marcacion_id != null);
    if (!hayMarcaciones) return;
    fetch(`${API_BASE}/api/tipos-marcacion`)
      .then((res) => (res.ok ? res.json() : []))
      .then((tipos: { id: number; nombre: string }[]) => {
        setNombresMarcacion(Object.fromEntries(tipos.map((t) => [t.id, t.nombre])));
      })
      .catch(() => {
        // Sin bloquear el render — la meta simplemente omite el nombre de marcación
      });
  }, [lineas]);

  return (
    <Box sx={{ marginTop: '24px' }}>
      <Typography
        sx={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: fs(11),
          fontWeight: 600,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: TOKENS.muted,
          marginBottom: '8px',
        }}
      >
        PRODUCTOS · {lineas.length}
      </Typography>

      {lineas.length === 0 ? (
        <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', padding: '14px' }}>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), color: TOKENS.text, marginBottom: '6px' }}>
            Sin productos — agrega líneas desde la edición completa
          </Typography>
          <Typography
            data-testid="quote-lines-ver-edicion"
            onClick={onVerEdicion}
            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.purple, cursor: 'pointer' }}
          >
            Ver / editar líneas completas →
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            data-testid="quote-lines-scroll"
            sx={{
              maxHeight: '295px',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: TOKENS.faint, borderRadius: '999px' },
            }}
          >
            {lineas.map((linea) => (
              <FilaLinea
                key={linea.id}
                linea={linea}
                nombreMarcacion={linea.tipo_marcacion_id != null ? nombresMarcacion[linea.tipo_marcacion_id] : undefined}
              />
            ))}
          </Box>

          {lineas.length > MAX_FILAS_VISIBLES && (
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted, marginTop: '6px' }}>
              Desplázate para ver las {lineas.length - MAX_FILAS_VISIBLES} líneas restantes
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: `1px solid ${TOKENS.hair}`,
            }}
          >
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink }}>
              TOTAL VENTA
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(14.5), fontWeight: 700, color: TOKENS.ink }}>
              {formatMoneda(total)}
            </Typography>
          </Box>

          <Typography
            data-testid="quote-lines-ver-edicion"
            onClick={onVerEdicion}
            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12.5), fontWeight: 600, color: TOKENS.purple, cursor: 'pointer', marginTop: '10px' }}
          >
            Ver / editar líneas completas →
          </Typography>
        </>
      )}
    </Box>
  );
};
